import * as Api from '@ViewModels';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { MastheadPortalId } from '../../../models/AdminModels';
import { NavigationBreadcrumbsBar } from '../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../web/components/Portal';
import { CustomSurvey } from '../../../web/containers/SurveyCreation/CustomSurvey';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { SaveSurveyTemplateModal } from '../../containers/templates/SurveyTemplates/SaveSurveyTemplateModal';

/**
 * Admin page for creating new survey templates.
 *
 * This wraps the existing CustomSurvey editor in templateMode to:
 * - Hide QR Code button (templates don't have survey links)
 * - Change Save button to "Save as Template"
 * - Capture survey definition instead of creating a survey
 * - Open SaveSurveyTemplateModal to collect template metadata
 */
export function AdminCreateSurveyTemplatePage() {
	const history = useHistory();
	const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
	const [surveyDefinition, setSurveyDefinition] = React.useState<Api.ICustomSurvey | null>(null);

	const handleSaveAsTemplate = React.useCallback((definition: Api.ICustomSurvey) => {
		setSurveyDefinition(definition);
		setIsSaveModalOpen(true);
	}, []);

	const handleTemplateCreated = () => {
		toast.success('Survey template created successfully');
		history.push('/surveys');
	};

	// Hide intermediate breadcrumb segments that don't have routes
	const pathComponentNameProvider = React.useCallback((pathComponent: string): string | null => {
		const names: Record<string, string> = {
			'survey-library': 'Survey library',
			new: 'New',
		};
		return names[pathComponent] ?? null;
	}, []);

	return (
		<AdminDashboardPageLayout>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />
			</Portal>

			<CustomSurvey templateMode={true} onSaveAsTemplate={handleSaveAsTemplate} />

			{surveyDefinition && (
				<SaveSurveyTemplateModal
					open={isSaveModalOpen}
					onOpenChange={setIsSaveModalOpen}
					surveyDefinition={surveyDefinition}
					onSuccess={handleTemplateCreated}
				/>
			)}
		</AdminDashboardPageLayout>
	);
}
